// =============================================================================
// ui/tabs/ResultsKpis.tsx — the Results board's six headline tiles. #58.
// -----------------------------------------------------------------------------
// Transcribed from beta's own rendered board, not from its source: the page was
// served locally with a known 40-sample payload and the tiles read out of the
// live DOM. That is what settled the labels, the sub-lines and the verdict pill
// — beta's file still carries markup for things the board no longer shows.
//
//   Net points  ·  Samples  ·  Win rate  ·  Expectancy  ·  Profit factor  ·  Edge ratio
//
// ── ⚠ THE HERO TILE CARRIES A VERDICT, AND IT IS NOT DECORATION ──
//
// "▲ Edge holding" / "▼ No edge" is the one sentence on the board that makes a
// claim rather than reporting a number, so it says nothing at all when there is
// not enough to say it from.
//
// ── 🚨 THE R SUB-LINE IS WHERE THIS CLIENT DIVERGES ON PURPOSE ──
//
// Beta's Profit-factor tile reads "+0.67R avg" underneath. That average is
// computed over every sample INCLUDING those with no stop, which beta counts as
// zero R (see domain/backtestBoard.ts). Here the average excludes them — so the
// sub-line also states HOW MANY samples it covered whenever that is fewer than
// the book. An average over 6 of 200 is a different claim from one over 200, and
// a bare number cannot tell the trader which they are reading.
// =============================================================================

import type { BoardStats } from '../../selectors/backtestBoard';
import { num, pct } from '../format';

/**
 * ⚠ Not a rule beta states anywhere — it renders the pill at any size. Below
 * this the sign of the net is one or two samples' luck, and a green "Edge
 * holding" on four trades is the board's most quotable claim being made from
 * nothing.
 */
const VERDICT_MIN_SAMPLES = 20;

function Tile(
  { label, value, sub, cls }:
  { label: string; value: string; sub?: string | undefined; cls?: string | undefined },
) {
  return (
    <div className="bd-kpi">
      <div className="bd-k-label">{label}</div>
      <div className={`bd-k-val${cls ? ` ${cls}` : ''}`}>{value}</div>
      {sub ? <div className="bd-k-sub">{sub}</div> : null}
    </div>
  );
}

export interface ResultsKpisProps {
  stats: BoardStats;
}

export function ResultsKpis({ stats }: ResultsKpisProps) {
  const {
    n, wins, losses, winRate, net, expectancy, profitFactor, edgeRatio, avgR, nR,
  } = stats;

  // ⚠ An empty slice replaces all six tiles with one line. Six em-dashes look
  // like a broken board; one sentence says which filter emptied it.
  if (n === 0) {
    return (
      <div className="bd-kpis bd-kpis-empty" data-testid="results-kpis">
        <div className="bd-kpi bd-hero">
          <div className="bd-k-label">No samples</div>
          <div className="bd-k-sub">
            Nothing matches this instrument and pattern. Clear a filter to widen it.
          </div>
        </div>
      </div>
    );
  }

  const verdict = n >= VERDICT_MIN_SAMPLES
    ? (net > 0 ? { text: '▲ Edge holding', cls: 'up' } : { text: '▼ No edge', cls: 'down' })
    : null;

  // 🚨 The count, whenever the R average did not cover every sample. See header.
  const rSub = avgR == null
    ? undefined
    : nR === n
      ? `${net >= 0 ? '+' : ''}${num(avgR, 2)}R avg`
      : `${net >= 0 ? '+' : ''}${num(avgR, 2)}R avg over ${nR} of ${n}`;

  return (
    <div className="bd-kpis" data-testid="results-kpis">
      <div className="bd-kpi bd-hero">
        <div className="bd-k-label">Net points</div>
        <div className={`bd-k-val ${net >= 0 ? 'green' : 'red'}`}>
          {net >= 0 ? '+' : ''}{num(net, 1)}<small> pts</small>
        </div>
        {verdict ? <div className={`bd-verdict ${verdict.cls}`}>{verdict.text}</div> : null}
      </div>

      <Tile
        label="Samples" value={String(n)}
        sub={`${wins}W · ${losses}L decided`}
      />
      <Tile
        label="Win rate" value={winRate == null ? '—' : pct(winRate, 0)}
        sub="of decided trades"
      />
      <Tile
        label="Expectancy"
        value={expectancy == null ? '—' : `${expectancy >= 0 ? '+' : ''}${num(expectancy, 2)}`}
        sub="pts / trade"
        cls={expectancy == null ? undefined : expectancy >= 0 ? 'green' : 'red'}
      />
      <Tile
        label="Profit factor"
        value={profitFactor == null ? '—' : num(profitFactor, 2)}
        sub={rSub}
      />
      <Tile
        label="Edge ratio"
        value={edgeRatio == null ? '—' : num(edgeRatio, 2)}
        sub="avg MFE / MAE"
      />
    </div>
  );
}

export { VERDICT_MIN_SAMPLES };
